/* Tus empresas — la cartera (pantalla 5a del rediseño).
 *
 * El supuesto que rompe esta pantalla: un consultor lleva cuatro o diez empresas a la vez y la
 * aplicación asumía una. Cada empresa tiene su RUP, sus territorios, su capacidad, su lista de
 * seguimiento y sus descartes, y LA AFINIDAD SE CALCULA POR EMPRESA — un mismo proceso puede ser
 * excelente para una y un desperdicio para otra. Esa frase es el producto entero de esta pantalla.
 *
 * Tres decisiones que conviene no deshacer:
 *
 * 1. La completitud del perfil se calcula AQUÍ, con `contarFaltantes` de la página de perfil. No la
 *    devuelve el servidor: `GET /api/cartera` manda hechos crudos (`tiene_rup`, `tiene_perfil`) y la
 *    definición de «qué le falta a un perfil» tiene un solo dueño, el cliente. Por eso esta página
 *    pide el perfil de cada empresa — es una petición por empresa, en paralelo, sobre una cartera de
 *    veinticinco como mucho.
 * 2. El «N le sirven hoy» sale de `POST /api/oportunidades/buscar` con `empresa_id`, que puntúa con
 *    esa empresa SIN activarla. Activar y desactivar para preguntar reordenaría la bandeja de las
 *    demás pestañas, que comparten la sesión.
 * 3. Mientras las cifras llegan se muestra un guion, no un cero. Un cero es una afirmación.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { borrar, enviar, ErrorApi, obtener, reemplazar } from '../api/cliente'
import { entero, normalizarNombre } from '../api/formato'
import type {
  Cartera, EmpresaCartera, Perfil, RespuestaBusqueda, RespuestaPerfil,
} from '../api/tipos'
import { PERFIL_VACIO, usePerfil } from '../estado/perfil'
import { contarFaltantes } from './Perfil'
import { IconoMas, IconoQuitar } from '../ui/Iconos'
import './Empresas.css'

/** Las cuatro cosas que pesan en la afinidad; ver `contarFaltantes`. */
const TOTAL_FALTANTES = 4

/**
 * Cuántos «le sirven» a una empresa: los de las dos bandas que dicen que vale la pena mirarlos.
 *
 * `compite` y `cautela` y no las cuatro, porque las otras dos se llaman «Piénsalo dos veces» y
 * «Mejor no»: contarlas aquí sería decir que sirve algo que la propia pantalla desaconseja.
 *
 * Sin perfil no hay bandas y la respuesta es NULA, no cero: «cero le sirven» afirma que se miró y
 * no había ninguno, cuando lo cierto es que no había con qué mirar.
 */
function leSirven(r: RespuestaBusqueda): number | null {
  const b = r.conteo_bandas
  if (!b) return null
  const conBanda = b.compite + b.cautela + b.piensalo + b.mejor_no
  return conBanda === 0 ? null : b.compite + b.cautela
}

/** Lo que se va sabiendo de cada empresa, que llega en tres tandas y por separado. */
type Detalle = {
  faltan: number | null
  sirven: number | null
  cierran: number | null
  siguiendo: number | null
  /** ¿Entra en el modo «Todas» de la bandeja? Sale del perfil; ausente = sí. */
  compara: boolean
  /** El perfil completo y su versión, para poder guardar el interruptor sin volver a pedirlo. */
  perfil: Perfil | null
  version: number
}

export function Empresas() {
  const navegar = useNavigate()
  const { recargar: recargarPerfil } = usePerfil()
  const [cartera, setCartera] = useState<Cartera | null>(null)
  const [detalle, setDetalle] = useState<Record<number, Detalle>>({})
  const [error, setError] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const cargar = useCallback(async () => {
    try {
      setCartera(await obtener<Cartera>('/api/cartera'))
      setError(null)
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  /* Las cifras de cada empresa, en paralelo y cada una por su lado: si la del cubo falla —o el cubo
     no está—, la completitud del perfil se ve igual. Una tarjeta a medias informa; una tarjeta que
     no aparece porque una de tres peticiones falló, no. */
  useEffect(() => {
    if (!cartera) return
    const corte = new AbortController()
    for (const e of cartera.empresas) {
      const anota = (parcial: Partial<Detalle>) =>
        setDetalle((d) => ({
          ...d,
          [e.id]: {
            faltan: null, sirven: null, cierran: null, siguiendo: null,
            compara: true, perfil: null, version: 0,
            ...d[e.id], ...parcial,
          },
        }))

      obtener<RespuestaPerfil>(`/api/perfil?empresa_id=${e.id}`, corte.signal)
        .then((r) => anota({
          faltan: contarFaltantes({ ...PERFIL_VACIO, ...(r.perfil ?? {}) }, r.rup),
          siguiendo: Object.values(r.perfil?.watch ?? {}).filter((m) => m.estado === 'sigo').length,
          compara: r.perfil?.comparar !== false,
          perfil: r.perfil,
          version: r.version,
        }))
        .catch(() => {})
      /* ⚠ «Le sirven hoy» sale de `conteo_bandas`, NO de `total`. `total` es cuántos pasan los
         filtros, y el perfil solo cambia el ORDEN: dos empresas con perfiles opuestos tienen el
         mismo `total`, así que llamarlo «le sirven» sería una cifra que parece del perfil y no lo
         es. Se vio en pantalla — dos empresas muy distintas mostrando el mismo 61. */
      enviar<RespuestaBusqueda>('/api/oportunidades/buscar',
                                { empresa_id: e.id, limit: 1 }, corte.signal)
        .then((r) => anota({ sirven: leSirven(r) })).catch(() => {})
      enviar<RespuestaBusqueda>('/api/oportunidades/buscar',
                                { empresa_id: e.id, limit: 1, dias_max: 7 }, corte.signal)
        .then((r) => anota({ cierran: leSirven(r) })).catch(() => {})
    }
    return () => corte.abort()
  }, [cartera])

  const activar = useCallback(async (id: number, ir?: string) => {
    setOcupado(true)
    try {
      await reemplazar('/api/cartera/activa', { empresa_id: id })
      // El perfil en memoria es el de la empresa ANTERIOR: sin recargarlo, la bandeja de la
      // siguiente pantalla ordenaría con uno y marcaría con el otro.
      await recargarPerfil()
      await cargar()
      if (ir) navegar(ir)
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : String(e))
    } finally {
      setOcupado(false)
    }
  }, [cargar, navegar, recargarPerfil])

  const anadir = useCallback(async () => {
    setOcupado(true)
    try {
      await enviar('/api/cartera', {})
      await recargarPerfil()
      navegar('/perfil')
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : String(e))
      setOcupado(false)
    }
  }, [navegar, recargarPerfil])

  /**
   * Encender o apagar una empresa para el modo «Todas».
   *
   * Se guarda EN LA CUENTA, dentro del perfil de esa empresa, con su `empresa_id` explícito. No en
   * la sesión ni en el navegador: la cartera es de la cuenta y el triaje de la mañana tiene que ser
   * el mismo mañana y desde otra máquina.
   */
  const alternarComparar = useCallback(async (e: EmpresaCartera) => {
    const d = detalle[e.id]
    if (!d?.perfil) return
    const nuevo = !d.compara
    setDetalle((prev) => ({ ...prev, [e.id]: { ...(prev[e.id] as Detalle), compara: nuevo } }))
    try {
      const r = await reemplazar<RespuestaPerfil>('/api/perfil', {
        perfil: { ...d.perfil, comparar: nuevo },
        version: d.version,
        empresa_id: e.id,
      })
      setDetalle((prev) => ({
        ...prev,
        [e.id]: { ...(prev[e.id] as Detalle), version: r.version, perfil: r.perfil },
      }))
    } catch (err) {
      // Se devuelve el interruptor a donde estaba: dejarlo movido diría que se guardó.
      setDetalle((prev) => ({ ...prev, [e.id]: { ...(prev[e.id] as Detalle), compara: !nuevo } }))
      setError(err instanceof ErrorApi ? err.message : String(err))
    }
  }, [detalle])

  const quitar = useCallback(async (e: EmpresaCartera) => {
    // Se pregunta, y con el nombre dentro: borrar una empresa se lleva su perfil, su RUP y su lista
    // de seguimiento, y no hay deshacer.
    const nombre = normalizarNombre(e.razon_social) || `NIT ${e.nit}` || 'esta empresa'
    if (!window.confirm(
      `¿Quitar ${nombre} de tu cartera?\n\n` +
      'Se borran su perfil, su RUP y su lista de seguimiento. No se puede deshacer.')) return
    setOcupado(true)
    try {
      await borrar(`/api/cartera/${e.id}`)
      await recargarPerfil()
      await cargar()
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : String(err))
    } finally {
      setOcupado(false)
    }
  }, [cargar, recargarPerfil])

  if (error && !cartera) {
    return (
      <div className="andamio">
        <h1>No pude leer tu cartera</h1>
        <p>{error}</p>
      </div>
    )
  }
  if (!cartera) return <p className="rotulo">Cargando</p>

  const totales = cartera.empresas.reduce(
    (acc, e) => {
      const d = detalle[e.id]
      return {
        sirven: d?.sirven != null ? (acc.sirven ?? 0) + d.sirven : acc.sirven,
        cierran: d?.cierran != null ? (acc.cierran ?? 0) + d.cierran : acc.cierran,
      }
    },
    { sirven: null as number | null, cierran: null as number | null },
  )

  return (
    <div className="empresas">
      <div className="empresas-cuerpo">
        <header className="empresas-cabeza">
          <div>
            <h1>Tus empresas</h1>
            <p>
              Cada una tiene su RUP, sus territorios, su capacidad y su lista de seguimiento. La
              afinidad se calcula <b>por empresa</b>: un mismo proceso puede ser excelente para una
              y un desperdicio para otra.
            </p>
          </div>
          <dl className="empresas-totales">
            <Total n={cartera.empresas.length} k={cartera.empresas.length === 1 ? 'empresa' : 'empresas'} />
            <Total n={totales.sirven} k="procesos les sirven hoy" />
            <Total n={totales.cierran} k="cierran esta semana" urgente />
          </dl>
        </header>

        {error && <p className="empresas-error">{error}</p>}

        {cartera.empresas.length === 0 ? (
          <p className="nota">
            Todavía no tienes ninguna empresa. Añade la primera y carga su RUP: con eso la bandeja
            se ordena por lo que le sirve, y no por criterios generales.
          </p>
        ) : (
          <ul className="cartera">
            {cartera.empresas.map((e) => (
              <Tarjeta key={e.id} e={e} d={detalle[e.id]} activa={e.id === cartera.activa_id}
                       ocupado={ocupado} onActivar={activar} onQuitar={quitar}
                       onComparar={alternarComparar} />
            ))}
          </ul>
        )}

        <div className="empresas-anadir">
          <div>
            <h2>Añadir una empresa</h2>
            <p>
              Se crea vacía y te lleva a su perfil. Ahí puedes cargar su RUP en PDF o escribir su
              NIT: con cualquiera de los dos queda lista en un minuto.
            </p>
          </div>
          <button type="button" className="boton-primario" onClick={() => void anadir()}
                  disabled={ocupado || cartera.empresas.length >= cartera.tope}>
            <IconoMas /> Añadir una empresa
          </button>
        </div>
        {cartera.empresas.length >= cartera.tope && (
          <p className="nota">
            Llegaste al tope de {entero(cartera.tope)} empresas por cuenta. Quita alguna para añadir
            otra, o escríbenos si de verdad necesitas más.
          </p>
        )}

        <p className="descargo">
          Lo que corriges en la ficha de un proceso —«sí trabajo en Caldas», «sí tengo experiencia en
          este tema»— se guarda en el perfil de <b>esa</b> empresa, no en las demás. Los descartes y
          el seguimiento también son por empresa.
        </p>
      </div>
    </div>
  )
}

/** Una cifra del encabezado. Guion mientras no se sabe: un cero afirmaría que no hay ninguno. */
function Total({ n, k, urgente }: { n: number | null; k: string; urgente?: boolean }) {
  return (
    <div>
      <dd className={`cifra${urgente && n ? ' urgente' : ''}`}>{entero(n)}</dd>
      <dt>{k}</dt>
    </div>
  )
}

function Tarjeta({ e, d, activa, ocupado, onActivar, onQuitar, onComparar }: {
  e: EmpresaCartera
  d: Detalle | undefined
  activa: boolean
  ocupado: boolean
  onActivar: (id: number, ir?: string) => void
  onQuitar: (e: EmpresaCartera) => void
  onComparar: (e: EmpresaCartera) => void
}) {
  const nombre = normalizarNombre(e.razon_social)
  const listo = d?.faltan == null ? null
    : Math.round(((TOTAL_FALTANTES - d.faltan) / TOTAL_FALTANTES) * 100)

  return (
    <li className={`empresa${activa ? ' activa' : ''}`}>
      <div className="empresa-quien">
        <h2>
          {nombre || <span className="empresa-sin-nombre">Empresa sin nombre</span>}
          {activa && <span className="empresa-activa-marca">Activa</span>}
        </h2>
        <p className="empresa-nit">{e.nit ? `NIT ${e.nit}` : 'Sin NIT declarado'}</p>
        {/* La barra NUNCA va sola: al lado va el porcentaje escrito y debajo qué falta. El color no
            informa por sí mismo, y una barra a medias sin decir de qué es solo inquieta. */}
        <div className="empresa-completo">
          <span className="empresa-barra" aria-hidden="true">
            <i style={{ width: `${listo ?? 0}%` }} className={listo === 100 ? 'lleno' : ''} />
          </span>
          <span>{listo === null ? 'Comprobando el perfil…' : `perfil ${listo} % listo`}</span>
        </div>
        <p className="empresa-alerta">
          {d?.faltan == null ? ' '
            : d.faltan === 0 ? 'Perfil completo.'
              : `Faltan ${entero(d.faltan)} ${d.faltan === 1 ? 'dato' : 'datos'} para que la afinidad sea suya y no la general.`}
        </p>
      </div>

      <div className="empresa-cifras">
        <p className="empresa-sirven">
          <b className="cifra">{entero(d?.sirven ?? null)}</b> <span>le sirven hoy</span>
        </p>
        <p className="empresa-detalle">
          {entero(d?.siguiendo ?? null)} en seguimiento · {entero(d?.cierran ?? null)} cierran esta
          semana
        </p>
      </div>

      <div className="empresa-acciones">
        {/* El interruptor del modo «Todas». Deshabilitado hasta que llegue el perfil: sin él no se
            puede guardar, y un interruptor que se mueve y no guarda es peor que uno apagado. */}
        <label className="empresa-comparar">
          <input type="checkbox" checked={d?.compara ?? true} disabled={!d?.perfil || ocupado}
                 onChange={() => onComparar(e)} />
          <span>Comparar en «Todas»</span>
        </label>
        <button type="button" className="boton-primario" disabled={ocupado}
                onClick={() => onActivar(e.id, '/')}>
          Ver sus oportunidades
        </button>
        <div className="par">
          {/* Editar el perfil de otra empresa la ACTIVA primero: el perfil se edita sobre la
              activa, y llevar a alguien a un formulario que guarda en otra empresa sería el fallo
              silencioso que el `empresa_id` de las escrituras viene a evitar. */}
          <button type="button" className="accion" disabled={ocupado}
                  onClick={() => onActivar(e.id, '/perfil')}>
            Editar perfil
          </button>
          <button type="button" className="accion" disabled={ocupado}
                  onClick={() => onQuitar(e)} title="Quitar de la cartera">
            <IconoQuitar /> Quitar
          </button>
        </div>
      </div>
    </li>
  )
}
